import React, { useState, useEffect } from 'react';

import { Grid, Divider } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { appendColorAndKeyToLabelObject } from 'screens/performanceReview/ResultPage/components/ResultSpiderChart/hooks/dataHooks';
import { useReview } from 'screens/performanceReview/ReviewContext';
import styled, { css } from 'styled-components';
import {
  ColoredCircularProgress,
  StyledTypography,
  StyledPaper,
  StyledBox,
  VerticallyAlignedFlexContainer,
  StyledTooltip,
} from 'styles/common';

import noCommentsImage from 'assets/images/action-points-placeholder.png';

import {
  VISIBLE_PROVIDER_TYPES_COLOR_MAPPING,
  REVIEW_PROVIDER_TYPES,
  RATINGS_TYPES,
  REVIEW_ANSWER_TYPE_MAP,
} from 'constants/ProjectConstants';
import { getColorFromKey } from 'constants/helper';
import { StarIcon } from 'constants/icons';
import theme from 'constants/theme';

import { getReviewRatingDetails } from 'services/reviews';
import { showErrorSnackbar } from 'services/snackbar';

import { Typography3, Typography4 } from 'uiToolkit/Typography';

import OverallRating from '../OverallRating';
import SpiderChart from './SpiderChart';

const DOMAIN_LOWER_LIMIT = 0;
const DOMAIN_UPPER_LIMIT = 5;

const StyledDivider = styled(Divider)`
  width: 80%;
  background-color: ${theme.palette.borders.lighterGrey};
`;

const StyledRatingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;

const StyledGrid = styled(Grid)`
  border-right: 1px solid ${theme.palette.borders.lighterGrey};
`;

const StyledRatingInnerContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: ${(props) => props.justifyContent ?? 'center'};
  padding: 12px 0;
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateX(-50%) translateY(-50%);
`;

const ImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  text-align: center;
`;

const StyledImage = styled.img`
  width: 16rem;
`;

const Container = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${(props) => (props?.size === 'big' ? '0.5rem 1rem' : '0.25rem 0.5rem')};
  min-height: 2rem;
  min-width: 4rem;
  border-radius: 4px;
`;
const StyledRatingBox = styled(Container)`
  ${css`
    background-color: ${(props) => getColorFromKey(props.borderColor)};
    color: ${(props) => getColorFromKey(props.borderColor)};
  `}
`;

const StyledVerticallyAlignedFlexContainer = styled(VerticallyAlignedFlexContainer)`
  width: 80%;
  justify-content: space-between;
  padding: 10px 0;
`;

function NoRatingImageComp({ textToDisplay = null, getTextByProviderType }) {
  return (
    <ImageContainer>
      <StyledImage src={noCommentsImage} />
      <StyledTypography style={{ whiteSpace: 'pre' }} topGutter={2} align="center" gutterBottom color="grey">
        {getTextByProviderType(textToDisplay)}
      </StyledTypography>
    </ImageContainer>
  );
}

function RatingPanel({ getTextByProviderType, rating, ratingRow, reviewCycleName }) {
  const ratingIsDropDown = rating?.ratingType === REVIEW_ANSWER_TYPE_MAP.DROP_DOWN;
  const overallValue = parseInt(rating?.ratingSplit?.find((r) => r.type === 'OVERALL')?.value, 10);
  const showRatingPanel = ratingIsDropDown ? true : overallValue > 0;

  if (!showRatingPanel) {
    return (
      <NoRatingImageComp
        textToDisplay="Thank you! We appreciate your contributions."
        getTextByProviderType={getTextByProviderType}
      />
    );
  }

  const onlyOverallRating = rating?.ratingSplit.length === 1;
  const ratingsWithoutOverall = !onlyOverallRating
    ? rating?.ratingSplit.map((r) => {
        if (r.type !== 'OVERALL') {
          return ratingRow(r.type, r.value, ratingIsDropDown);
        }
        return null;
      })
    : null;

  return onlyOverallRating ? (
    <>
      <StyledRatingInnerContainer>
        <StyledTypography variant="h6" gutterBottom>
          {RATINGS_TYPES.OVERALL}
        </StyledTypography>
        {reviewCycleName && (
          <StyledTypography gutterBottom color="grey">
            {reviewCycleName}
          </StyledTypography>
        )}
      </StyledRatingInnerContainer>
      <StyledDivider orientation="horizontal" />
      <StyledRatingInnerContainer>
        <OverallRating rating={rating} />
      </StyledRatingInnerContainer>
    </>
  ) : (
    <>
      <StyledRatingInnerContainer>
        <StyledTypography variant="h6" gutterBottom>
          {RATINGS_TYPES.OVERALL}
        </StyledTypography>
        {reviewCycleName && (
          <StyledTypography gutterBottom color="grey">
            {reviewCycleName}
          </StyledTypography>
        )}
        <OverallRating rating={rating} />
      </StyledRatingInnerContainer>
      <StyledDivider orientation="horizontal" />
      <StyledRatingInnerContainer justifyContent="flex-start" style={{ margin: '1rem 0' }}>
        {ratingsWithoutOverall}
      </StyledRatingInnerContainer>
    </>
  );
}

const RatingAndSpiderChart = ({ employeeId, reviewId, isReviewPublished }) => {
  const {
    state: {
      selectedReviewInfo: { providerEmployeeId, reviewProviderType, employeeName, reviewCycleName },
    },
  } = useReview();

  const dispatch = useDispatch();
  const [rating, setRating] = useState(null);
  const [loading, setLoading] = useState(false);

  const getRatings = () => {
    setLoading(true);
    getReviewRatingDetails({ reviewId, employeeId, providerId: providerEmployeeId })
      .then((res) => {
        let { providerTypes } = res;
        const providerKey = 'providerTypeKey';
        providerTypes = appendColorAndKeyToLabelObject({
          LabelObject: providerTypes,
          primaryKey: providerKey,
          colorMapper: VISIBLE_PROVIDER_TYPES_COLOR_MAPPING,
        });
        return {
          ...res,
          providerTypes,
        };
      })
      .then((res) => {
        setRating(res);
        if (res?.isShowOverallRating && !res?.ratingSplit?.length) {
          dispatch(showErrorSnackbar('Unable to fetch scores!'));
        }
      })
      .catch((err) => dispatch(showErrorSnackbar(err.message || 'Oops! something went wrong!')))
      .finally(() => setLoading(false));
  };

  const getTextByProviderType = (textToDisplay) => {
    if (textToDisplay) {
      return textToDisplay;
    }
    const { isShowOverallRating } = rating;
    const displayText = isShowOverallRating
      ? 'Thank you! We appreciate your contributions. \n Your assessment will be visible here.'
      : 'Thank you! We appreciate your contributions.';
    switch (reviewProviderType) {
      case REVIEW_PROVIDER_TYPES.PEER:
      case REVIEW_PROVIDER_TYPES.UPWARD:
        return `Thank you! ${employeeName} appreciates your feedback for ${reviewCycleName}`;
      default:
        return isReviewPublished ? 'Thank you! We appreciate your contributions.' : displayText;
    }
  };

  const ratingRow = (type, value, ratingIsDropDown) => (
    <StyledVerticallyAlignedFlexContainer>
      <Typography4>{RATINGS_TYPES[type]}</Typography4>
      <StyledRatingBox borderColor={theme.palette.background.seagreen}>
        <StarIcon color={theme.palette.icons.green} size="tinier" />
        <StyledTooltip title={ratingIsDropDown ? value : ''}>
          <div>
            <Typography3 leftGutter={1} color={theme.palette.text.green} truncate style={{ maxWidth: '10rem' }}>
              {value}
            </Typography3>
          </div>
        </StyledTooltip>
      </StyledRatingBox>
    </StyledVerticallyAlignedFlexContainer>
  );

  useEffect(() => {
    getRatings();
  }, [providerEmployeeId]);

  const spiderChartDomain = [DOMAIN_LOWER_LIMIT, rating?.competencyGraphData[0]?.maxRating || DOMAIN_UPPER_LIMIT];

  return (
    <StyledPaper bottomGutter={3} padding="1rem 0rem">
      <Grid container alignItems="stretch" justify="center">
        {loading && (
          <Grid item xs={3}>
            <StyledBox flexcontainer justifycontent="center" alignitems="center">
              <ColoredCircularProgress />
            </StyledBox>
          </Grid>
        )}
        {!loading &&
          (rating ? (
            <>
              {rating.isShowOverallRating ? (
                <StyledGrid item xs={12} sm={4}>
                  <StyledRatingContainer style={{ position: 'relative' }}>
                    {rating.isShowOverallRating && rating?.ratingSplit?.length && (
                      <RatingPanel
                        getTextByProviderType={getTextByProviderType}
                        rating={rating}
                        ratingRow={ratingRow}
                        reviewCycleName={reviewCycleName}
                      />
                    )}
                    {!(rating.isShowOverallRating && rating?.ratingSplit?.length) &&
                      (rating.competencyGraphData.length > 2 && rating.providerTypes.length > 0 ? (
                        <NoRatingImageComp />
                      ) : (
                        <ErrorContainer>
                          <StyledTypography color="textSecondary">Unable to fetch score data!</StyledTypography>
                        </ErrorContainer>
                      ))}
                  </StyledRatingContainer>
                </StyledGrid>
              ) : (
                <StyledGrid item xs={12} sm={4} style={{ position: 'relative', minHeight: '24rem' }}>
                  <NoRatingImageComp />
                </StyledGrid>
              )}
              <Grid item xs={12} sm={8} style={{ position: 'relative' }}>
                {rating && rating.competencyGraphData.length > 2 && rating.providerTypes.length > 0 ? (
                  <SpiderChart
                    data={rating.competencyGraphData}
                    LabelDetails={rating.providerTypes}
                    domain={spiderChartDomain}
                  />
                ) : (
                  <ErrorContainer>
                    <StyledTypography color="textSecondary">Not Enough Data to Show Chart!</StyledTypography>
                  </ErrorContainer>
                )}
              </Grid>
            </>
          ) : (
            <Grid item xs={12} sm={8} style={{ position: 'relative' }}>
              <ErrorContainer>
                <StyledTypography color="textSecondary">Unable to fetch score data!</StyledTypography>
              </ErrorContainer>
            </Grid>
          ))}
      </Grid>
    </StyledPaper>
  );
};

export default RatingAndSpiderChart;
